import React, { PureComponent } from "react";

import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, { textFilter } from "react-bootstrap-table2-filter";

import {
  MetricTable,
  sortNumeric,
  selectFormat,
  sortDate,
  Error,
  TableContentCell,
} from "components/Utils/Utils.js";

const metricFormatter = (cell, row, rowIndex, enumObject) => {
  const props = { ...cell, ...enumObject };
  return <MetricTable {...props} />;
};

const mixedMetricFormatter = (cell, row, rowIndex, enumObject) => {
  const props = { ...cell, ...enumObject };

  if (typeof cell.value === "string") {
    return cell.value;
  } else {
    return <MetricTable {...props} />;
  }
};

const dateFormatter = (cell) => {
  if (cell && cell.date) {
    return cell.title;
  } else {
    return cell;
  }
};

class AdsDataByNetwork extends PureComponent {
  componentDidMount() {
    this.props.childRef(this);
  }

  renderTable(data, columns, footer, pagination) {
    const { title } = this.props;

    const selected_columns = [];

    // add unique ids
    data.forEach((d, i) => (d.id = i));

    let defaultSortType = "date_title";

    if (title.includes("Content Tags")) {
      defaultSortType = "tag_views";
    } else if (title.includes("Targeting, Age, & Gender")) {
      defaultSortType = "ad_targeting";
    } else if (title.includes("Level")) {
      defaultSortType = "view_time";
    } else if (title.includes("App Marketing by Market")) {
      defaultSortType = "spend";
    } else if (title.includes("Data by Month")) {
      defaultSortType = "source";
    } else if (
      title === "Search Audience Targeting" ||
      title === "Search Audience Targeting & Keyword" ||
      title === "Search Audience Keyword" ||
      title === "Creative Type" ||
      title === "Social Audience & Targeting" ||
      title === "Social Audience"
    ) {
      defaultSortType = "impressions";
    } else if (title === "Campaign Summaries") {
      defaultSortType = "total_spend";
    } else {
      defaultSortType = "date_title";
    }

    const total_index = data.findIndex(({ source }) => source === "Total");
    let table_data = data.slice(0);
    const footer_data = footer
      ? table_data.splice(total_index, 1)[0]
      : table_data;

    const table_columns = [
      {
        dataField: "title",
        text: "Title",
        style: { width: "320px", minWidth: "320px", textAlign: "left" },
        headerAlign: "left",
        formatter: TableContentCell,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
        sort: true,
      },
      {
        dataField: "source",
        text: "Source",
        style: { width: "150px", minWidth: "150px", textAlign: "left" },
        headerAlign: "left",
        sort: true,
      },
      {
        dataField: "date_title",
        text: "Title",
        style: { width: "120px", minWidth: "120px" },
        headerAlign: "left",
        formatter: dateFormatter,
        csvFormatter: dateFormatter,
        sort: true,
        sortFunc: sortDate,
        footer: footer_data["date_title"],
        footerFormatter: (col) => dateFormatter(col.footer),
      },
      {
        dataField: "sign_ups",
        text: "Sign Ups",
        style: { width: "100px", minWidth: "100px" },
        headerAlign: "left",
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["sign_ups"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },
      {
        dataField: "purchases",
        text: "Purchases",
        style: { width: "80px", minWidth: "80px" },
        headerAlign: "left",
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["purchases"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },
      {
        dataField: "total_purchases",
        text: "Total Purchases",
        style: { width: "120px", minWidth: "120px" },
        headerAlign: "left",
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["total_purchases"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },
      {
        dataField: "landing_page_views",
        text: "Landing Page Views",
        style: { width: "120px", minWidth: "120px" },
        headerAlign: "left",
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["landing_page_views"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },
      {
        dataField: "landing_page_clicks",
        text: "Landing Page Clicks",
        style: { width: "80px", minWidth: "80px" },
        headerAlign: "left",
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["landing_page_clicks"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },
      {
        dataField: "market",
        text: "Market",
        style: { width: "100px", minWidth: "100px" },
        headerAlign: "left",
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
        footer: footer_data["market"],
      },

      {
        dataField: "ad_name",
        text: "Ad Name",
        style: { width: "140px", minWidth: "140px" },
        headerAlign: "left",
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
        footer: footer_data["ad_name"],
      },
      {
        dataField: "ad_type",
        text: "Ad Type",
        style: { width: "80px", minWidth: "80px" },
        headerAlign: "left",
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
        footer: footer_data["ad_name"],
      },
      {
        dataField: "network",
        text: "Network",
        style: { width: "120px", minWidth: "120px" },
        headerAlign: "left",
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
        footer: footer_data["network"],
      },
      {
        dataField: "ad_targeting",
        text: "Ad Targeting",
        style: { width: "150px", minWidth: "150px" },
        headerAlign: "left",
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
        footer: footer_data["ad_targeting"],
      },
      {
        dataField: "age",
        text: "Age",
        style: { width: "60px", minWidth: "60px" },
        headerAlign: "left",
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["age"],
      },
      {
        dataField: "gender",
        text: "Gender",
        style: { width: "60px", minWidth: "60px" },
        headerAlign: "left",
        sort: true,
        footer: footer_data["gender"],
      },
      {
        dataField: "keyword",
        text: "Keyword",
        style: { width: "80px", minWidth: "80px" },
        headerAlign: "left",
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
        footer: footer_data["keyword"],
      },
      {
        dataField: "impressions",
        text: "IMPs",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["impressions"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },
      {
        dataField: "activations",
        text: "Activations",
        style: { width: "60px", minWidth: "60px" },
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["activations"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },
      {
        dataField: "clicks",
        text: "Clicks",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        footer: footer_data["clicks"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },
      {
        dataField: "ctr",
        text: "CTR",
        style: { width: "70px", minWidth: "70px" },
        formatter: metricFormatter,
        formatExtraData: { type: "percentage", format: "0.00%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["ctr"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      {
        dataField: "free_trial_actives",
        text: "Free Trial Actives",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sortFunc: sortNumeric,
        footer: footer_data["free_trial_actives"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      {
        dataField: "free_trial_converters",
        text: "Free Trial Converters",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sortFunc: sortNumeric,
        footer: footer_data["free_trial_converters"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      {
        dataField: "free_trial_starts",
        text: "Free Trial Starts",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sortFunc: sortNumeric,
        footer: footer_data["free_trial_starts"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      // Custom formatting to accomodate strings and numbers

      {
        dataField: "free_trial_length",
        text: "Free Trial Length",
        style: { width: "80px", minWidth: "80px" },
        formatter: mixedMetricFormatter,
        csvFormatter: (cell) =>
          typeof cell.value === "number"
            ? selectFormat("csv_number", cell && cell.value, "0,0")
            : cell.value,
        sortFunc: sortNumeric,
        footer: footer_data["free_trial_length"],
        footerFormatter: (col) => mixedMetricFormatter(col.footer),
      },

      {
        dataField: "subscription_type",
        text: "Subscription Type",
        style: { width: "100px", minWidth: "100px" },
        formatter: (cell) => cell && cell.value,
        csvFormatter: (cell) => cell && cell.value,
        footer: footer_data["subscription_type"],
        footerFormatter: (col) => metricFormatter(col.footer),
      },

      {
        dataField: "cpc",
        text: "CPC",
        style: { width: "70px", minWidth: "70px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cpc"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cplv",
        text: "CPLV",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cplv"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cpi",
        text: "CPI",
        style: { width: "70px", minWidth: "70px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cpi"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cpa",
        text: "CPA",
        style: { width: "70px", minWidth: "70px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cpa"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cpts",
        text: "CPTS",
        style: { width: "90px", minWidth: "90px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cpts"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cptp",
        text: "CPTP",
        style: { width: "90px", minWidth: "90px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cptp"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cpv",
        text: "CPV",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cpv"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cpcv",
        text: "CPCV",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cpcv"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "views",
        text: "Views",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["views"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "views_unique",
        text: "Views Unique",
        style: { width: "100px", minWidth: "100px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["views"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "views_25",
        text: "Views 25%",
        style: { width: "90px", minWidth: "90px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["views_25"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "views_50",
        text: "Views 50%",
        style: { width: "90px", minWidth: "90px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["views_50"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "views_75",
        text: "Views 75%",
        style: { width: "90px", minWidth: "90px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["views_75"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "agency_cost",
        text: "Agency Cost",
        style: { width: "120px", minWidth: "120px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["agency_cost"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "recurring_subscribers_billings",
        text: "Recurring Subscribers Billings",
        style: { width: "150px", minWidth: "150px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["recurring_subscribers_billings"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      {
        dataField: "subscribers_rebill",
        text: "Subscribers Up For Rebill",
        style: { width: "130px", minWidth: "130px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["subscribers_rebill"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      {
        dataField: "total_subscribers",
        text: "Total Subscribers",
        style: { width: "120px", minWidth: "120px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["total_subscribers"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      {
        dataField: "total_subs",
        text: "Total Subscriptions",
        style: { width: "130px", minWidth: "130px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["total_subs"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      {
        dataField: "client_cost",
        text: "Client Cost",
        style: { width: "100px", minWidth: "100px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["client_cost"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "total_spent",
        text: "Total Spent",
        style: { width: "100px", minWidth: "100px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["total_spent"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "spend",
        text: "Spend",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["spend"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },

      {
        dataField: "total_spend",
        text: "Total Spend",
        style: { width: "100px", minWidth: "100px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["total_spend"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "total_client_spent",
        text: "Total Client Spent",
        style: { width: "140px", minWidth: "140px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["total_client_spent"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cost",
        text: "Cost",
        style: { width: "100px", minWidth: "100px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cost"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cpm",
        text: "CPM",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cpm"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "cpa_new_sub_starts",
        text: "CPA New Sub Starts",
        style: { width: "140px", minWidth: "140px" },
        formatter: metricFormatter,
        formatExtraData: { type: "currency" },
        csvFormatter: (cell) =>
          selectFormat("csv_currency", cell && cell.value, "$0,0.00"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["cpa_new_sub_starts"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
      {
        dataField: "tags",
        text: "Tags",
        style: { width: "140px", minWidth: "140px" },
        headerAlign: "left",
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
      },
      {
        dataField: "tag_views",
        text: "Views",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "viewers",
        text: "Unique Viewers",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "view_time",
        text: "Min Watched",
        style: { width: "100px", minWidth: "100px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "view_time_vs_pluto",
        text: "Min % of Pluto",
        style: { width: "110px", minWidth: "110px" },
        formatter: metricFormatter,
        formatExtraData: { type: "percentage", format: "0.00%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "pluto_brand_rank",
        text: "Brand Rank",
        style: { width: "100px", minWidth: "100px" },
        csvFormatter: (cell) => cell.value,
        sort: true,
      },
      {
        dataField: "pluto_season_rank",
        text: "Season Rank",
        style: { width: "100px", minWidth: "100px" },
        csvFormatter: (cell) => cell.value,
        sort: true,
      },
      {
        dataField: "season_title",
        text: "Season Title",
        style: { width: "80px", minWidth: "80px" },
        csvFormatter: (cell) => cell.value,
        sort: true,
      },
      {
        dataField: "view_time_vs_total",
        text: "Min % of Catalog",
        style: { width: "120px", minWidth: "120px" },
        formatter: metricFormatter,
        formatExtraData: { type: "percentage", format: "0.00%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
      },

      {
        dataField: "first_streams",
        text: "1st Streams",
        style: { width: "110px", minWidth: "110px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "first_streams_vs_pluto",
        text: "1st Streams % Pluto",
        style: { width: "130px", minWidth: "130px" },
        formatter: metricFormatter,
        formatExtraData: { type: "percentage", format: "0.00%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "first_streams_vs_total",
        text: "1st Strems % Catalog",
        style: { width: "140px", minWidth: "140px" },
        formatter: metricFormatter,
        formatExtraData: { type: "percentage", format: "0.00%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "views_unique_vs_pluto",
        text: "UV % vs Pluto",
        style: { width: "110px", minWidth: "110px" },
        formatter: metricFormatter,
        formatExtraData: { type: "percentage", format: "0.00%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "views_unique_vs_total",
        text: "UV % vs Catalog",
        style: { width: "120px", minWidth: "120px" },
        formatter: metricFormatter,
        formatExtraData: { type: "percentage", format: "0.00%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
      },

      {
        dataField: "installs",
        text: "Installs",
        style: { width: "80px", minWidth: "80px" },
        formatter: metricFormatter,
        formatExtraData: { type: "number" },
        csvFormatter: (cell) => selectFormat("number", cell && cell.value),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "free_trial_conversion_rate",
        text: "Free Trial Conversion Rate",
        style: { width: "100px", minWidth: "100px" },
        formatter: metricFormatter,
        formatExtraData: { type: "percentage", format: "0.00%" },
        csvFormatter: (cell) =>
          selectFormat("percentage", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
        footer: footer_data["free_trial_conversion_rate"],
        footerFormatter: (col) =>
          metricFormatter(col.footer, null, null, col.formatExtraData),
      },
    ];

    let paginate = null;

    columns.map((column) =>
      selected_columns.push(table_columns.find((o) => o.dataField === column))
    );

    const defaultSorted = [
      {
        dataField: defaultSortType,
        order: "desc",
      },
    ];

    if (pagination === true) {
      let sizePerPagination = 10;

      if (title.includes("App Marketing by Market")) {
        sizePerPagination = 20;
      }
      paginate = paginationFactory({
        sizePerPage: sizePerPagination,
        hideSizePerPage: true,
      });
    }

    const filename = title.split(" ").join("_") || "spreadsheet";

    return (
      <ToolkitProvider
        keyField="id"
        data={table_data}
        columns={selected_columns}
        exportCSV={{
          fileName: filename + ".csv",
          exportAll: false,
          onlyExportFiltered: true,
        }}
        striped={false}
        hover={false}
        bordered={false}
      >
        {(props) => (
          <BootstrapTable
            {...props.baseProps}
            {...props.csvProps}
            defaultSorted={defaultSorted}
            pagination={paginate}
            filter={filterFactory()}
            footerClasses={footer ? "tfoot__tr" : "tfoot__tr d-none"}
            ref={(el) => (this.childRef = el)}
          />
        )}
      </ToolkitProvider>
    );
  }

  builtInExportCSV = () => this.childRef;

  render() {
    const { data, columns, footer, pagination } = this.props;
    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    return this.renderTable(data.data, columns, footer, pagination);
  }
}

export default AdsDataByNetwork;
